import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaUserCircle, FaBars, FaTimes } from 'react-icons/fa';
import logo from "../../assets/images/tnslogo.png";
import config from "../../config/apiConfig";
import { useAuth, endServerSession } from "../../auth/session";

const NavBar = ({ authenticated, setAuthenticated }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // const [demoMenuOpen, setDemoMenuOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  const handleLogout = async () => {
    // Front-door sign-in only (no-op otherwise): end the server-side session
    // before leaving, so the refresh token is revoked rather than just the
    // Cognito cookie cleared.
    await endServerSession();
    sessionStorage.clear();
    const countryPref = localStorage.getItem("countrypref");
    localStorage.clear();
    localStorage.setItem("countrypref", countryPref);
    window.location.href = `${config.cognito.cognitoDomain}/logout?client_id=${config.cognito.clientId}&logout_uri=${encodeURIComponent(config.cognito.logoutUri)}`;
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
    navigate('/login');
  };

  // Navigation items
  const navItems = [
    { label: 'Ask', href: '/ask', path: '/ask' },
  ];

  // Tools dropdown items
  const toolsItems = [
    { label: 'Solution Builder', href: '/solutionbuilder', path: '/solutionbuilder' },
    { label: 'Marketing Content Builder', href: '/marketingcontentbuilder', path: '/marketingcontentbuilder' },
    { label: 'Copilot Agents', href: '/agents', path: '/agents' },
    { label: 'GTM on a Page', href: 'https://tnsius.sharepoint.com/:x:/s/TEAM-Fintech-Payments-RGO/EYZK6tb_WYRIj7ff4FDTtTsBknKib8i7Gr_ond9WPr8-hA?e=XvnHtK', external: true },
    { label: 'Developer Portal', href: 'https://developer.pay.tnsi.com', external: true },
    { label: 'More Resources', href: 'https://tnsius.sharepoint.com/sites/products/payments/SitePages/TNSPay%20Orchestration.aspx', external: true },
  ];

  // Demo is now a single page link (no dropdown)

  const isActive = (path) => pathname === path;

  const getLinkClasses = (path, mobile = false) => {
    const base = mobile 
      ? "block px-4 py-2 text-base font-medium rounded-full"
      : "px-4 py-2 text-sm font-medium rounded-full";
    
    return isActive(path)
      ? `${base} bg-white text-blue-600 shadow-sm`
      : `${base} bg-blue-500 text-white hover:bg-white hover:text-blue-600 transition-colors border border-blue-400`;
  };

  if (!authenticated) {
    return (
      /* ROLLBACK: Original was "bg-gradient-to-r from-blue-700 to-blue-400" */
      <nav className="bg-blue-600 shadow-sm relative z-50">
        <div className="px-4">
          <div className="flex items-center justify-between h-16">
            <a href="/">
              <img className="h-14 w-auto" src={logo} alt="TNS" />
            </a>
          </div>
        </div>
      </nav>
    );
  }

  return (
    /* ROLLBACK: Original was "bg-gradient-to-r from-blue-700 to-blue-400" */
    <nav className="bg-blue-600 shadow-sm relative z-50">
      <div className="px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/">
            <img className="h-14 w-auto" src={logo} alt="TNS" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={getLinkClasses(item.path)}
              >
                {item.label}
              </a>
            ))}
            
            {/* Tools Dropdown */}
            <div className="relative">
              <button
                onClick={() => setToolsMenuOpen(!toolsMenuOpen)}
                className={`px-4 py-2 text-sm font-medium bg-blue-500 text-white hover:bg-white hover:text-blue-600 transition-colors border border-blue-400 rounded-full flex items-center space-x-1`}
              >
                <span>Tools</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {toolsMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                  {toolsItems.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setToolsMenuOpen(false)}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                    >
                      {item.label}
                      {item.external && (
                        <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
            
            {/* Payment Demo Link */}
            <a
              href="/paymentdemo"
              className={getLinkClasses('/paymentdemo')}
            >
              Payment Demo
            </a>

            <button onClick={handleLogout} className="px-4 py-2 text-sm font-medium bg-blue-500 text-white hover:bg-white hover:text-blue-600 transition-colors border border-blue-400 rounded-full">
              Logout
            </button>
          </div>

          {/* User Menu (Desktop) */}
          <div className="hidden md:block relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-white"
            >
              <FaUserCircle size="2em" className="text-white" />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-white rounded-md shadow-lg py-1 z-50">
                <a href="/ask" className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Ask</a>
                <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Logout</button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-white hover:bg-blue-800 focus:outline-none"
          >
            {mobileMenuOpen ? <FaTimes size="1.5em" /> : <FaBars size="1.5em" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-3">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className={getLinkClasses(item.path, true)}
                >
                  {item.label}
                </a>
              ))}
              
              {/* Tools section in mobile */}
              <div className="pt-2">
                <div className="text-white text-sm font-medium px-4 py-2">Tools</div>
                {toolsItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block px-6 py-2 text-sm text-white hover:bg-blue-700 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                  >
                    {item.label}
                    {item.external && (
                      <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    )}
                  </a>
                ))}
              </div>
              
              {/* Payment Demo link in mobile */}
              <a
                href="/paymentdemo"
                className={getLinkClasses('/paymentdemo', true)}
                onClick={() => setMobileMenuOpen(false)}
              >
                Payment Demo
              </a>
              
              <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-base font-medium bg-blue-500 text-white hover:bg-white hover:text-blue-600 transition-colors border border-blue-400 rounded-full">
                Logout
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Overlay to close user menu when clicking outside */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setUserMenuOpen(false)}
        />
      )}



      {/* Overlay to close tools menu when clicking outside */}
      {toolsMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setToolsMenuOpen(false)}
        />
      )}
    </nav>
  );
};

export default NavBar;